import { observable, action, toJS, makeObservable } from 'mobx';
import mixpanel from 'mixpanel-browser';
import { pinEventMutation, userEventsQuery } from 'graphql/event';

import { client as apolloClient } from 'util/apolloClient';
import { client as api2Client } from 'util/api2Client';
import {
  userActivityById,
  userJoinedCauzesQuery,
  userProfileByEmail,
  userProfileById,
  userProfileCompanyMatchById,
  userProfileDataById,
} from 'graphql/user';
import {
  followMutationV2,
  unfollowMutationV2,
  updateFollowerMutation,
} from 'graphql/follow';
import { trackFollow, trackUnfollow } from 'util/tracking/follow';
import {
  parseLocationQuery,
  userFeedQuery,
  likeMutationV2,
  unlikeMutationV2,
} from 'graphql/feed';
import {
  setProfileSettingsMutation,
  updateProfileMutation,
} from 'graphql/profile';
import { quickSearchUsersQuery } from 'graphql/users';

const LEDGER_PAGE_SIZE = 200;

class UserProfileStore {
  @observable loading = true;
  @observable feedLoading = true;
  @observable feedLoadingMore = false;
  @observable ledgerLoading = true;
  @observable ledgerLoadingMore = false;
  @observable ledgerFilters = {};
  @observable ledgerHasMore = true;
  @observable eventsLoading = true;
  @observable joinedEventsLoading = true;
  @observable isError = false;
  @observable data;
  @observable userFeed;
  @observable totalPages;
  @observable currentPage = 1;
  @observable currentLedgerPage = 1;
  @observable users = new Map();
  @observable feeds = new Map();
  @observable events = new Map();
  @observable supportedCharities = new Map();
  @observable charities = new Map();
  @observable joinedEvents = new Map();
  @observable updatingZip = false;
  @observable companyMatch = null;

  @action setCharities = ({ followerId, charities }) => {
    this.charities.set(+followerId, charities);
  };

  @action setLedgerFilters = (filters) => {
    this.ledgerFilters = filters;
  };

  @action getUserForModal = async ({ id }) => {
    const options = {
      variables: { query: `${id}` },
      query: quickSearchUsersQuery,
      fetchPolicy: 'network-only',
    };

    if (this.users.has(+id)) {
      return this.users.get(+id);
    }

    const result = await api2Client.query(options);
    return result.data.quickSearchUsers[0];
  };

  @action getUser = async ({ id, silent = false, checkCache = false }) => {
    if (!silent) {
      this.loading = true;
    }

    const options = {
      variables: { userId: parseInt(id) },
      query: userProfileById,
      fetchPolicy: 'network-only',
      errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
    };

    try {
      let result;
      let resultData;

      if (checkCache) {
        result = await this.getUserForModal({ id });

        if (result) {
          resultData = {
            ...result,
            profileData: {
              followerCount: result.followerCount,
              followingCount: result.followingCount,
              userGood: {
                alltimeGoodSum: result?.totalGood,
                alltimeInfluencedCount: result?.influenced,
              },
            },
          };

          this.users.set(+id, resultData);
          this.loading = false;
        }
      }

      result = await api2Client.query(options);

      resultData = {
        ...this.users.get(+id),
        ...resultData,
        ...result.data.data,
      };

      this.users.set(+id, resultData);
      this.charities.set(+id, result.data.data.featuredProjects);

      this.loading = false;
      this.isError = false;

      result = await api2Client.query({
        variables: { userId: parseInt(id) },
        query: userProfileDataById,
        fetchPolicy: 'network-only',
        errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
      });

      if (result.data.profileData) {
        resultData = {
          ...resultData,
          profileData: result.data.profileData,
        };

        this.users.set(+id, resultData);
      }

      return resultData;
    } catch (err) {
      this.loading = false;
      this.isError = true;
      this.error = err;
    }

    return {};
  };

  @action getCompanyMatch = async ({ id }) => {
    const options = {
      variables: {
        userId: parseInt(id),
        startFrom: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000),
      },
      query: userProfileCompanyMatchById,
    };

    try {
      const results = await api2Client.query(options);
      this.companyMatch = results.data.profileData.companyMatch;
      return this.companyMatch;
    } catch (err) {
      // pass
    }

    return null;
  };

  @action getUserByEmail = async ({ email }) => {
    const options = {
      variables: { email },
      query: userProfileByEmail,
      fetchPolicy: 'network-only',
      errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
    };

    try {
      const result = await api2Client.query(options);
      return result.data.userByEmail;
    } catch (err) {
      this.loading = false;
      this.isError = true;
      this.error = err;
    }

    return {};
  };

  @action getUserProfileDataForModal = async ({ id }) => {
    const options = {
      variables: { userId: parseInt(id) },
      query: userProfileDataById,
      fetchPolicy: 'network-only',
    };

    if (this.users.has(+id)) {
      return this.users.get(+id);
    }

    const result = await api2Client.query(options);
    return result.data.profileData;
  };

  @action getUserActivity = async ({ id, loadMore = false, filters }) => {
    if (!loadMore) {
      this.currentLedgerPage = 1;
      this.ledgerLoading = true;
      this.ledgerHasMore = true;
    } else {
      this.currentLedgerPage += 1;
      this.ledgerLoadingMore = true;
    }

    const options = {
      variables: {
        userId: parseInt(id),
        offset: (this.currentLedgerPage - 1) * LEDGER_PAGE_SIZE,
        limit: LEDGER_PAGE_SIZE,
        ...filters,
      },
      query: userActivityById,
      fetchPolicy: 'network-only',
      errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
    };
    try {
      const result = await api2Client.query(options);
      const userActivity =
        result?.data?.userProfile?.userBalanceCreditsAndDebits || [];

      if (!userActivity?.length || userActivity.length < LEDGER_PAGE_SIZE) {
        this.ledgerHasMore = false;
      }

      return userActivity;
    } catch (err) {
      // ignore
    } finally {
      this.ledgerLoading = false;
      this.ledgerLoadingMore = false;
    }
  };

  @action getFeed = async ({ id }) => {
    this.feedLoading = true;
    this.currentPage = 1;

    try {
      const variables = {
        userId: parseInt(id),
        limit: 20,
        offset: 0,
      };

      const options = {
        variables: variables,
        query: userFeedQuery,
        fetchPolicy: 'no-cache',
        errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
      };

      const results = await api2Client.query(options);

      this.feeds.set(+id, results.data.userFeed);
    } catch (err) {
      // pass
    }

    this.feedLoading = false;
  };

  @action getEvents = async ({ id }) => {
    this.eventsLoading = true;

    try {
      const variables = {
        userId: parseInt(id),
        pinnedOnly: true,
      };

      const options = {
        variables: variables,
        query: userEventsQuery,
        fetchPolicy: 'no-cache',
        errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
      };

      const results = await api2Client.query(options);
      this.events.set(+id, results.data.userEvents);
      return results.data.userEvents;
    } catch {
      // pass
    } finally {
      this.eventsLoading = false;
    }

    return [];
  };

  @action getUserJoinedEvents = async ({ id }) => {
    this.joinedEventsLoading = true;

    try {
      const variables = {
        limit: 100,
      };

      const options = {
        variables,
        query: userJoinedCauzesQuery,
        fetchPolicy: 'no-cache',
        errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
      };

      const results = await api2Client.query(options);
      this.joinedEvents.set(+id, results.data.joinedCauzes);
      return results.data.joinedCauzes;
    } catch {
      // pass
    }

    this.joinedEventsLoading = false;
    return [];
  };

  @action loadMore = async ({ id }) => {
    this.currentPage = this.currentPage + 1;
    this.feedLoadingMore = true;
    const currentFeedData = toJS(this.feeds.get(+id));

    if (this.currentPage > this.totalPages) {
      return;
    }

    try {
      const variables = {
        userId: parseInt(id),
        limit: 20,
        offset: 20 * (this.currentPage - 1),
      };

      const options = {
        variables: variables,
        query: userFeedQuery,
        fetchPolicy: 'no-cache',
        errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
      };

      const results = await api2Client.query(options);
      this.feeds.set(+id, currentFeedData.concat(results.data.userFeed));
    } catch (err) {
      // pass
    }

    this.feedLoadingMore = false;
  };

  @action like = ({ like = true, id, purchaseId, userId }) => {
    const currentFeed = toJS(this.feeds.get(+userId));
    const feedItem = currentFeed.find(
      (feedItem) => feedItem?.activityPurchase?.id === id,
    );

    if (!feedItem) return;

    feedItem.currentEntityLiked = like;
    feedItem.likeCount = like ? feedItem.likeCount + 1 : feedItem.likeCount - 1;
    this.feeds.set(userId, currentFeed);

    const options = {
      variables: { id: parseInt(purchaseId) },
      mutation: like ? likeMutationV2 : unlikeMutationV2,
      errorPolicy: global.IS_DEV ? 'all' : 'none',
    };

    api2Client.mutate(options).catch(() => {
      feedItem.currentEntityLiked = !like;
      feedItem.likeCount = like
        ? feedItem.likeCount - 1
        : feedItem.likeCount + 1;
      this.feeds.set(+userId, currentFeed);
    });
  };

  @action updateFeedItemOptimistically = ({ index, updatedProps }) => {
    const feeds = this.feeds;

    if (!this.feeds) {
      return;
    }

    feeds[index] = {
      ...toJS(feeds[index]),
      ...updatedProps,
      ...(updatedProps.commentCount
        ? {
          commentCount: updatedProps.commentCount,
        }
        : {}),
      ...(updatedProps.likesCount
        ? {
          likesCount: feeds[index].likeCount + updatedProps.likeCount,
        }
        : {}),
    };
    this.feeds = toJS(feeds);
  };

  @action follow = async ({ follow = true, actorContext, userId }) => {
    const targetContext = { userId };
    this.setFollow({ userId, follow });

    if (follow) {
      trackFollow({ actorContext, targetContext });
    } else {
      trackUnfollow({ actorContext, targetContext });
    }

    let entityId = null;
    let entityType = null;
    if (targetContext.userId) {
      entityId = targetContext.userId;
      entityType = 'USER';
    } else if (targetContext.charityId) {
      entityId = targetContext.charityId;
      entityType = 'CHARITY';
    } else if (targetContext.companyId) {
      entityId = targetContext.companyId;
      entityType = 'COMPANY';
    }

    const options = {
      variables: {
        entityId: parseInt(entityId),
        entityType,
      },
      mutation: follow ? followMutationV2 : unfollowMutationV2,
      errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
    };

    await api2Client.mutate(options).catch((err) => {
      this.setFollow({ userId, follow: !follow });
      if (global.IS_LOCAL_OR_DEV) {
        console.error(err);
      }
    });
  };

  @action setFollow = ({ userId, follow }) => {
    const userData = this.users.get(+userId);

    if (userData) {
      userData.isSelfFollowing = follow;
      userData.followerCount = userData.followerCount + (follow ? 1 : -1);
      this.users.set(+userId, userData);
    }
  };

  @action pinEvent = ({ eventId, userId, pinned = true }) => {
    const event = this.events
      .get(+userId)
      ?.find(
        (event) => eventId && event.id?.toString() === eventId?.toString(),
      );

    if (!event) return;

    event.currentEntityHasPinned = pinned;

    mixpanel.track('Pin Event', { eventId });

    const variables = {
      eventId: parseInt(eventId),
      pinned: Boolean(pinned),
    };

    const options = {
      variables: variables,
      mutation: pinEventMutation,
      errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
    };

    api2Client.mutate(options).catch(() => {
      event.currentEntityHasPinned = pinned;
    });
  };

  @action resetStore = () => {
    apolloClient.resetStore();
  };

  @action updateZipFromParsedLocation = async ({ location, id }) => {
    this.updatingZip = true;
    let options = {
      variables: {
        location,
      },
      query: parseLocationQuery,
      errorPolicy: global.IS_DEV ? 'all' : 'none',
      fetchPolicy: 'no-cache',
    };
    try {
      await apolloClient.query(options);

      options = {
        variables: { id, zip: location },
        mutation: updateProfileMutation,
        errorPolicy: global.IS_DEV ? 'all' : 'none',
      };
      await apolloClient.mutate(options);

      const user = this.users.get(+id);

      if (user) {
        this.users.set(+id, {
          ...user,
          zip: location,
        });
      }
    } catch (err) {
      console.error(err);
    }
    this.updatingZip = false;
  };

  updateFollow = async ({ charityId, priority }) => {
    const variables = {
      charityId: parseInt(charityId),
      priority: parseInt(priority),
    };

    const options = {
      variables: variables,
      mutation: updateFollowerMutation,
      errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
    };

    await api2Client.mutate(options);
  };

  @action setProfileSettingsFlags = async (
    userId,
    hasClosedJoinOthersAndTrackInfluenceFlag,
  ) => {
    const user = this.users.get(+userId);
    if (!user) return;

    const originalFlags = { ...user.profileData.settings.flags };

    user.profileData.settings.flags = {
      ...user.profileData.settings.flags,
      ...hasClosedJoinOthersAndTrackInfluenceFlag,
    };
    this.users.set(+userId, user);

    try {
      await api2Client.mutate({
        mutation: setProfileSettingsMutation,
        variables: {
          flags: {
            hasClosedJoinOthersAndTrackInfluence:
              user.profileData.settings.flags
                .hasClosedJoinOthersAndTrackInfluence,
            hasCompletedOnboarding:
              user.profileData.settings.flags.hasCompletedOnboarding,
            hasPromptedForFoundationName:
              user.profileData.settings.flags.hasPromptedForFoundationName,
          },
        },
      });
    } catch (error) {
      // Rollback on error
      user.profileData.settings.flags = originalFlags;
      this.users.set(+userId, user);
      throw error;
    }
  };

  constructor() {
    makeObservable(this);
  }
}

const userProfileStore = new UserProfileStore();
export default userProfileStore;
